<template>
  <div class="development-kpi pa-4" :style="themeStyles">
    <!-- Header -->
    <div class="d-flex align-center mb-3">
      <h3 class="ma-0 text-h6">DEVELOPMENT</h3>
      <v-spacer></v-spacer>
      <v-chip small v-if="hasData">
        {{ developerRows.length }} developers
      </v-chip>
    </div>

    <!-- Step 1: date range + load -->
    <div class="controls-wrapper mb-4">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                hide-details
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              no-title
              @input="startDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" md="3">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                hide-details
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              no-title
              :min="startDate"
              @input="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" md="3" class="d-flex">
          <v-btn
            color="primary"
            :disabled="!canLoad"
            :loading="loading"
            small
            @click="loadData"
          >
            <v-icon left small>mdi-sync</v-icon>
            Load
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Data Table -->
    <div class="kpi-table-wrapper" v-if="hasData">
      <div class="table-container">
        <table class="main-table">
          <thead>
            <tr>
              <th class="row-number">#</th>
              <th class="developer-name">Developer</th>
              <th class="period-header" colspan="6">
                {{ periodHeader }}
              </th>
            </tr>
            <tr>
              <th class="row-number"></th>
              <th class="developer-name"></th>
              <th class="col-task">task #</th>
              <th class="col-reported">(reported time)</th>
              <th class="col-commits">Commits</th>
              <th class="col-merged">Merged</th>
              <th class="col-requests">Requests without comments</th>
              <th class="col-branches">Branches</th>
            </tr>
          </thead>
          <tbody>
            <!-- Developer Rows -->
            <tr
              v-for="(row, index) in developerRows"
              :key="'dev-' + index"
              class="developer-row"
            >
              <td class="row-number">{{ index + 1 }}</td>
              <td class="developer-name">
                <span class="dev-name-text">{{ row.author }}</span>
              </td>
              <td class="col-task">
                <v-select
                  v-model="row.selectedTaskKey"
                  :items="taskOptionsFor(row)"
                  dense
                  hide-details
                  @change="onTaskChange(row)"
                ></v-select>
              </td>
              <td class="col-reported text-right"
                :class="{ 'has-value': selectedTaskFor(row) && selectedTaskFor(row).reportedTime > 0 }"
              >{{ selectedTaskFor(row) ? selectedTaskFor(row).reportedTime : '-' }}</td>
              <td class="col-commits text-right">{{ row.gitlab ? row.gitlab.commits : '' }}</td>
              <td class="col-merged text-right">{{ row.gitlab ? row.gitlab.merged : '' }}</td>
              <td class="col-requests text-right">{{ row.gitlab ? row.gitlab.requestsWithoutComments : '' }}</td>
              <td class="col-branches text-right">{{ row.gitlab ? row.gitlab.branches : '' }}</td>
            </tr>

            <!-- Total Row -->
            <tr class="total-row">
              <td class="row-number"></td>
              <td class="developer-name total-label">Total</td>
              <td class="col-task"></td>
              <td class="col-reported text-right total-value">{{ totals.reportedTime }}</td>
              <td class="col-commits text-right total-value">{{ totals.commits }}</td>
              <td class="col-merged text-right total-value">{{ totals.merged }}</td>
              <td class="col-requests text-right total-value">{{ totals.requestsWithoutComments }}</td>
              <td class="col-branches text-right total-value branches-total">{{ totals.branches }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state text-center pa-8">
      <v-icon size="64" color="grey lighten-1">mdi-chart-timeline-variant-shimmer</v-icon>
      <p class="text-subtitle-1 grey--text mt-4">
        Select a date range, then click <strong>Load</strong> to fetch Jira worklogs and show only developers
        who have <strong>commits</strong> on a personal <strong>DEV</strong> MR in GitLab. SCM metrics fill automatically;
        change the task in the dropdown to switch metrics from the Load cache.
      </p>
    </div>

    <!-- Step 3: Save period -->
    <div class="save-period-wrapper mt-4" v-if="hasData">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="sprintName"
            label="Period name"
            dense
            hide-details
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            small
            color="primary"
            :disabled="!canSavePeriod"
            :loading="saving"
            @click="savePeriod"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Period
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Action Bar -->
    <div class="d-flex justify-end mt-3" v-if="hasData">
      <v-btn
        small
        color="primary"
        outlined
        class="mr-3"
        @click.prevent="exportToXLSX"
      >
        Export To XLSX
      </v-btn>
    </div>

    <!-- Comparison section -->
    <div class="comparison-wrapper mt-6">
      <h4 class="mb-2">Compare two periods</h4>
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <div class="d-flex align-center period-select-row">
            <v-select
              v-model="periodA"
              :items="savedPeriods"
              label="Period A"
              dense
              hide-details
              outlined
              class="flex-grow-1"
            ></v-select>
            <v-btn
              icon
              small
              class="ml-1 flex-shrink-0"
              color="error"
              :disabled="!periodA || deletingPeriod"
              :loading="deletingPeriod && periodToDelete === periodA"
              @click="askDeletePeriod(periodA)"
            >
              <v-icon small>mdi-delete-circle</v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="d-flex align-center period-select-row">
            <v-select
              v-model="periodB"
              :items="savedPeriods"
              label="Period B"
              dense
              hide-details
              outlined
              class="flex-grow-1"
            ></v-select>
            <v-btn
              icon
              small
              class="ml-1 flex-shrink-0"
              color="error"
              :disabled="!periodB || deletingPeriod"
              :loading="deletingPeriod && periodToDelete === periodB"
              @click="askDeletePeriod(periodB)"
            >
              <v-icon small>mdi-delete-circle</v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            small
            color="primary"
            :disabled="!periodA || !periodB"
            :loading="comparing"
            @click="comparePeriods"
          >
            Compare
          </v-btn>
        </v-col>
      </v-row>

      <ConfirmOrDeniedDialog
        v-if="deletePeriodDialog"
        v-model="deletePeriodDialog"
        :mode="confirmOrDeniedDialogModes.delete"
        :message="deletePeriodMessage"
        @onDeleteClick="deletePeriod"
        @onCancelClick="cancelDeletePeriod"
      />

      <v-row dense class="mt-3" v-if="comparisonResult">
        <v-col cols="12" md="4">
          <v-card outlined class="pa-3 kpi-card">
            <div class="kpi-card-label">[SCM] Pull requests without reviewer's comments</div>
            <div class="kpi-card-value">{{ comparisonResult.requestsWithoutComments }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-3 kpi-card">
            <div class="kpi-card-label">[SCM] Commits in relation to the estimates</div>
            <div class="kpi-card-value">{{ comparisonResult.commitsPerEstimate.toFixed(2) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-3 kpi-card">
            <div class="kpi-card-label">[SCM] Pull requests per branch</div>
            <div class="kpi-card-value">{{ comparisonResult.mergesPerBranch.toFixed(2) }}</div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import ConfirmOrDeniedDialog from '@/components/global/ConfirmOrDeniedDialog.vue'
import { formatLocalDate } from '@/utils/dateUtils'

const xlsx = require('json-as-xlsx')

export default {
  name: 'DevelopmentKPI',
  components: { ConfirmOrDeniedDialog },
  props: {
    kpiType: { type: String, required: true }
  },
  data () {
    return {
      // Date range
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,

      // Loading states
      loading: false,
      saving: false,
      comparing: false,
      deletingPeriod: false,

      // Data
      developerRows: [],

      // Save period
      sprintName: null,

      // Compare periods
      savedPeriods: [],
      periodA: null,
      periodB: null,
      comparisonResult: null,
      deletePeriodDialog: false,
      periodToDelete: null
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser || {}
    },
    selectedProject () {
      return this.$store.getters.getSelectedProject
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    deletePeriodMessage () {
      if (!this.periodToDelete) return ''
      return `Delete period "${this.periodToDelete}" and all saved developer rows for this period?`
    },
    isDark () {
      return this.$vuetify.theme.dark
    },
    themeStyles () {
      return {
        '--dev-bg': this.isDark ? '#1e1e1e' : '#ffffff',
        '--dev-bg-header': this.isDark ? '#1565C0' : '#4FC3F7',
        '--dev-bg-subheader': this.isDark ? '#1976D2' : '#81D4FA',
        '--dev-border': this.isDark ? '#444' : '#ccc',
        '--dev-text': this.isDark ? '#e0e0e0' : '#000000',
        '--dev-text-header': '#ffffff',
        '--dev-bg-total': this.isDark ? '#2a2a2a' : '#E3F2FD',
        '--dev-highlight': this.isDark ? '#FF7043' : '#FF5722',
        '--dev-value-color': this.isDark ? '#FFB74D' : '#E65100',
        '--dev-bg-branches': this.isDark ? '#3a3a3a' : '#EEEEEE'
      }
    },
    canLoad () {
      return !!(this.startDate && this.endDate && this.selectedProject)
    },
    canSavePeriod () {
      return this.hasData && !!this.sprintName
    },
    hasData () {
      return this.developerRows.length > 0
    },
    periodHeader () {
      if (!this.startDate && !this.endDate) return 'Period'
      return `${this.startDate || '?'} – ${this.endDate || '?'}`
    },
    totals () {
      return this.developerRows.reduce((acc, row) => {
        const task = this.selectedTaskFor(row)
        acc.reportedTime += task ? (parseFloat(task.reportedTime) || 0) : 0
        acc.commits += row.gitlab ? (row.gitlab.commits || 0) : 0
        acc.merged += row.gitlab ? (row.gitlab.merged || 0) : 0
        acc.requestsWithoutComments += row.gitlab ? (row.gitlab.requestsWithoutComments || 0) : 0
        acc.branches += row.gitlab ? (row.gitlab.branches || 0) : 0
        return acc
      }, { reportedTime: 0, commits: 0, merged: 0, requestsWithoutComments: 0, branches: 0 })
    }
  },
  watch: {
    selectedProject: {
      immediate: true,
      handler (newVal) {
        this.developerRows = []
        this.comparisonResult = null
        this.periodA = null
        this.periodB = null
        if (newVal) {
          this.loadSavedPeriods()
        }
      }
    }
  },
  methods: {
    selectedTaskFor (row) {
      if (!row.selectedTaskKey) return null
      return row.tasks.find(t => t.key === row.selectedTaskKey) || null
    },

    taskOptionsFor (row) {
      const options = row.tasks.map(t => ({
        text: `${t.key} — ${t.reportedTime}h — ${(t.summary || '').slice(0, 40)}`,
        value: t.key
      }))
      options.unshift({ text: '-', value: null })
      return options
    },

    mapGitlabMetrics (metrics) {
      if (!metrics) {
        return { commits: 0, merged: 0, requestsWithoutComments: 0, branches: 0 }
      }
      return {
        commits: metrics.commits || 0,
        merged: metrics.merged || 0,
        requestsWithoutComments: metrics.requestsWithoutComments || 0,
        branches: metrics.branches || 0
      }
    },

    onTaskChange (row) {
      if (!row.selectedTaskKey) {
        row.gitlab = null
        return
      }
      const task = this.selectedTaskFor(row)
      row.gitlab = task ? this.mapGitlabMetrics(task.gitlab) : null
    },

    async loadData () {
      if (!this.canLoad) return
      this.loading = true
      this.developerRows = []
      try {
        const res = await this.axios.post('/api/jira-velocity/get-development-report', {
          startDate: this.startDate,
          endDate: this.endDate,
          projectId: this.selectedProject
        }, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })

        const rows = (res.data || []).map(dev => {
          const tasks = (dev.tasks || []).map(task => ({
            ...task,
            gitlab: this.mapGitlabMetrics(task.gitlab)
          }))
          const selectedTaskKey = tasks.length ? tasks[0].key : null
          return {
            author: dev.author || 'Unassigned',
            employeeId: dev.employeeId,
            tasks,
            selectedTaskKey,
            gitlab: selectedTaskKey ? tasks[0].gitlab : null
          }
        })

        this.developerRows = rows
        this.sprintName = this.startDate && this.endDate ? `${this.startDate} – ${this.endDate}` : null
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: 'Failed to load data: ' + (err.response?.data || err.message)
        })
      } finally {
        this.loading = false
      }
    },

    async savePeriod () {
      if (!this.canSavePeriod) return
      this.saving = true
      try {
        const reports = this.developerRows.map(row => {
          const task = this.selectedTaskFor(row)
          return {
            Employee_ID: row.employeeId,
            Employee_Name: row.author,
            Task_Number: task ? task.taskNumber : '',
            Jira_Task_Key: task ? task.key : null,
            Reported_Time: task ? (task.reportedTime || 0) : 0,
            Commits: row.gitlab ? (row.gitlab.commits || 0) : 0,
            Merged: row.gitlab ? (row.gitlab.merged || 0) : 0,
            Requests_Without_Comments: row.gitlab ? (row.gitlab.requestsWithoutComments || 0) : 0,
            Branches: row.gitlab ? (row.gitlab.branches || 0) : 0
          }
        })

        await this.axios.post('/api/kpi/development-report', {
          Project_ID: this.selectedProject,
          Sprint_Name: this.sprintName,
          Sprint_Start: this.startDate,
          Sprint_End: this.endDate,
          Reports: reports
        }, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })

        this.$store.dispatch('setSuccess', { successMsg: 'Period saved successfully' })
        await this.loadSavedPeriods()
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: 'Failed to save period: ' + (err.response?.data || err.message)
        })
      } finally {
        this.saving = false
      }
    },

    async loadSavedPeriods () {
      if (!this.selectedProject) return
      try {
        const res = await this.axios.get(`/api/kpi/development-report-periods/${this.selectedProject}`, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.savedPeriods = (res.data || []).map(p => p.Sprint_Name)
      } catch (err) {
        console.warn('Failed to load saved periods:', err.message)
      }
    },

    askDeletePeriod (sprintName) {
      if (!sprintName) return
      this.periodToDelete = sprintName
      this.deletePeriodDialog = true
    },

    cancelDeletePeriod () {
      this.deletePeriodDialog = false
      this.periodToDelete = null
    },

    async deletePeriod () {
      if (!this.periodToDelete || !this.selectedProject) return
      this.deletingPeriod = true
      try {
        await this.axios.delete(
          `/api/kpi/development-report/${this.selectedProject}/${encodeURIComponent(this.periodToDelete)}`,
          { headers: { Authorization: 'Bearer ' + this.user.token } }
        )
        if (this.periodA === this.periodToDelete) this.periodA = null
        if (this.periodB === this.periodToDelete) this.periodB = null
        this.comparisonResult = null
        this.$store.dispatch('setSuccess', { successMsg: 'Period deleted successfully' })
        await this.loadSavedPeriods()
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: 'Failed to delete period: ' + (err.response?.data || err.message)
        })
      } finally {
        this.deletingPeriod = false
        this.deletePeriodDialog = false
        this.periodToDelete = null
      }
    },

    async comparePeriods () {
      if (!this.periodA || !this.periodB || !this.selectedProject) return
      this.comparing = true
      try {
        const [resA, resB] = await Promise.all([
          this.axios.get(`/api/kpi/development-report/${this.selectedProject}/${encodeURIComponent(this.periodA)}`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/kpi/development-report/${this.selectedProject}/${encodeURIComponent(this.periodB)}`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        ])

        const rows = [...(resA.data || []), ...(resB.data || [])]
        const sum = (field) => rows.reduce((acc, r) => acc + (parseFloat(r[field]) || 0), 0)

        const totalReportedTime = sum('Reported_Time')
        const totalCommits = sum('Commits')
        const totalMerged = sum('Merged')
        const totalBranches = sum('Branches')

        this.comparisonResult = {
          requestsWithoutComments: sum('Requests_Without_Comments'),
          commitsPerEstimate: totalReportedTime ? totalCommits / totalReportedTime : 0,
          mergesPerBranch: totalBranches ? totalMerged / totalBranches : 0
        }
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: 'Failed to compare periods: ' + (err.response?.data || err.message)
        })
      } finally {
        this.comparing = false
      }
    },

    // --- Export ---
    exportToXLSX () {
      if (!this.developerRows.length) {
        this.$store.dispatch('setError', { errorMsg: 'No data to export' })
        return
      }

      try {
        const columns = [
          { label: '#', value: 'rowNum' },
          { label: 'Developer', value: 'name' },
          { label: 'Task #', value: 'taskNumber' },
          { label: 'Reported Time', value: 'reportedTime' },
          { label: 'Commits', value: 'commits' },
          { label: 'Merged', value: 'merged' },
          { label: 'Requests w/o Comments', value: 'requestsWithoutComments' },
          { label: 'Branches', value: 'branches' }
        ]

        const content = this.developerRows.map((row, i) => {
          const task = this.selectedTaskFor(row)
          return {
            rowNum: i + 1,
            name: row.author,
            taskNumber: task ? task.key : '-',
            reportedTime: task ? task.reportedTime : 0,
            commits: row.gitlab ? row.gitlab.commits : '',
            merged: row.gitlab ? row.gitlab.merged : '',
            requestsWithoutComments: row.gitlab ? row.gitlab.requestsWithoutComments : '',
            branches: row.gitlab ? row.gitlab.branches : ''
          }
        })

        content.push({
          rowNum: '',
          name: 'Total',
          taskNumber: '',
          reportedTime: this.totals.reportedTime,
          commits: this.totals.commits,
          merged: this.totals.merged,
          requestsWithoutComments: this.totals.requestsWithoutComments,
          branches: this.totals.branches
        })

        if (this.comparisonResult) {
          const emptyRow = {
            rowNum: '',
            name: '',
            taskNumber: '',
            reportedTime: '',
            commits: '',
            merged: '',
            requestsWithoutComments: '',
            branches: ''
          }
          content.push({
            ...emptyRow,
            name: "[SCM] Pull requests without reviewer's comments",
            requestsWithoutComments: this.comparisonResult.requestsWithoutComments
          })
          content.push({
            ...emptyRow,
            name: '[SCM] Commits in relation to the estimates',
            commits: this.comparisonResult.commitsPerEstimate.toFixed(2)
          })
          content.push({
            ...emptyRow,
            name: '[SCM] Pull requests per branch',
            merged: this.comparisonResult.mergesPerBranch.toFixed(2)
          })
        }

        const data = [{
          sheet: `Dev KPI (${this.startDate} - ${this.endDate})`.slice(0, 31),
          columns,
          content
        }]

        const settings = {
          fileName: `Dev_KPI_${formatLocalDate()}`,
          extraLength: 3,
          writeOptions: {}
        }

        xlsx(data, settings)
        this.$store.dispatch('setSuccess', { successMsg: 'Data exported successfully' })
      } catch (error) {
        this.$store.dispatch('setError', {
          errorMsg: 'Export failed: ' + error.message
        })
      }
    }
  }
}
</script>

<style scoped>
.development-kpi {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--dev-bg);
  color: var(--dev-text);
}

.controls-wrapper,
.save-period-wrapper,
.comparison-wrapper {
  background: var(--dev-bg);
  border: 1px solid var(--dev-border);
  border-radius: 8px;
  padding: 12px 16px;
}

.period-select-row {
  width: 100%;
  min-width: 0;
}

.period-select-row > .v-input {
  min-width: 0;
}

/* Table Wrapper */
.kpi-table-wrapper {
  flex: 1;
  border: 1px solid var(--dev-border);
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
  background: var(--dev-bg);
}

.table-container {
  width: 100%;
  min-width: 1000px;
}

.main-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header Styles */
.main-table thead tr:first-child th {
  background: var(--dev-bg-header);
  color: var(--dev-text-header);
  font-weight: 700;
  font-size: 13px;
  text-align: center;
  padding: 10px 8px;
  border: 1px solid var(--dev-border);
}

.main-table thead tr:nth-child(2) th {
  background: var(--dev-bg-subheader);
  color: var(--dev-text-header);
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  padding: 8px 6px;
  border: 1px solid var(--dev-border);
  white-space: nowrap;
}

.period-header {
  border-left: 3px solid #0D47A1 !important;
}

/* Cell Styles */
.main-table td {
  border: 1px solid var(--dev-border);
  padding: 6px 8px;
  height: 40px;
  vertical-align: middle;
  font-size: 13px;
  color: var(--dev-text);
}

/* Column widths */
.row-number {
  width: 40px;
  min-width: 40px;
  text-align: center;
  color: #999;
  font-size: 11px;
}

.developer-name {
  width: 160px;
  min-width: 160px;
  font-weight: 500;
}

.col-task {
  width: 210px;
  min-width: 210px;
}

.col-reported {
  width: 95px;
  min-width: 95px;
}

.col-commits {
  width: 75px;
  min-width: 75px;
}

.col-merged {
  width: 75px;
  min-width: 75px;
}

.col-requests {
  width: 220px;
  min-width: 220px;
}

.col-branches {
  width: 85px;
  min-width: 85px;
}

/* Developer row */
.developer-row:hover {
  background: rgba(66, 165, 245, 0.08);
}

.dev-name-text {
  color: #1565C0;
  font-weight: 600;
}

.has-value {
  color: var(--dev-value-color);
  font-weight: 600;
}

/* Total Row */
.total-row {
  background: var(--dev-bg-total) !important;
}

.total-row td {
  font-weight: 700;
  border-top: 2px solid var(--dev-border);
}

.total-label {
  font-weight: 800 !important;
  font-size: 14px !important;
}

.total-value {
  color: var(--dev-highlight);
  font-weight: 700;
}

.branches-total {
  background: var(--dev-bg-branches) !important;
}

/* Text alignment */
.text-right {
  text-align: right !important;
}

/* Empty state */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  border: 2px dashed var(--dev-border);
  border-radius: 12px;
}

/* Comparison KPI cards */
.kpi-card {
  background: var(--dev-bg);
  border-color: var(--dev-border) !important;
}

.kpi-card-label {
  font-size: 12px;
  color: var(--dev-text);
  opacity: 0.8;
  margin-bottom: 4px;
}

.kpi-card-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--dev-highlight);
}

/* Responsive input styling */
.controls-wrapper ::v-deep .v-input--is-focused .v-input__slot {
  border-color: #1976D2 !important;
}
</style>
